import { useState } from 'react'

export interface UsePromiseReturn<T, P extends any[] = never> {
  loading: boolean
  result?: T
  perform: (...args: P) => Promise<T>
}

interface DefaultValues<T> { defaultResultValue?: T; defaultLoadingValue?: boolean }
function usePromise<T, P extends any[]>(
  promise: (...args: P) => Promise<T>,
  {
    defaultResultValue,
    defaultLoadingValue
  }: DefaultValues<T>  = { }
): UsePromiseReturn<T, P> {
  const [result, setResult] = useState<T>(defaultResultValue as T)
  const [loading, setLoading] = useState<boolean>(defaultLoadingValue ?? false)

  const perform = (...args: P): Promise<T> => {
    return new Promise((resolve, reject) => {
      setLoading(true)
      if (result) setResult(undefined as T)
      promise(...args)
        .then((_result) => {
          setResult(_result)
          resolve(_result)
        })
        .catch((error) => reject(error))
        .finally(() => {
          setLoading(false)
        })
    })
  }

  return { result, perform, loading }
}

export interface UseCachedPromiseReturn<T, P extends any[] = never>
  extends UsePromiseReturn<T, P> {
  clearCache: () => void
  cacheLessPerform: (...args: P) => Promise<T>
}

const generateCachedPromiseHook = (PROMISE_CACHE: Map<string, any>) =>
  function _useCachedPromise<T, P extends any[]>(
    promise: (...args: P) => Promise<T>,
    cacheKey: string
  ): UseCachedPromiseReturn<T, P> {
    const [result, setResult] = useState<T>()
    const { perform: _perform, loading } = usePromise(promise)

    const cacheLessPerform = async (...args: P): Promise<T> => {
      const _res = await _perform(...args)
      PROMISE_CACHE.set(cacheKey, _res)

      setResult(_res)
      return _res
    }

    const perform = async (...args: P): Promise<T> => {
      if (PROMISE_CACHE.has(cacheKey)) {
        console.log('###perform-' + cacheKey, PROMISE_CACHE.get(cacheKey))
        setResult(PROMISE_CACHE.get(cacheKey))
        return PROMISE_CACHE.get(cacheKey) as T
      }

      const _res = await _perform(...args)
      PROMISE_CACHE.set(cacheKey, _res)

      setResult(_res)
      return _res
    }

    const clearCache = () => {
      if (PROMISE_CACHE.has(cacheKey)) PROMISE_CACHE.delete(cacheKey)
    }

    return { result, perform, cacheLessPerform, clearCache, loading }
  }

const _cache = new Map<string, any>()

const useInMemoryCachedPromise = generateCachedPromiseHook(_cache)

export { useInMemoryCachedPromise }
export default usePromise
